import { Box, CircularProgress } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useView } from "../../../hooks/useView";
import V2Search from "../search/V2Search";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function V2ViewResults() {
  const { customerId, viewId } = useParams<{ customerId: string; viewId: string }>();
  const { user: authUser } = useContext(AuthContext);
  const { t } = useTranslation("common");

  // -- Feature flag gate ------------------------------------------------------
  const hasViewsFlag = authUser?.hasFeatureFlag("Views") ?? false;

  // -- View metadata ----------------------------------------------------------
  const { data: view, isLoading: viewLoading } = useView(customerId, viewId);

  // -- Format date helper ---------------------------------------------------
  const formatExpiration = (retentionDate?: number | null): string => {
    if (!retentionDate) return "";
    return new Date(retentionDate * 1000).toLocaleDateString();
  };

  // -- Feature flag gate render ---------------------------------------------
  if (!hasViewsFlag) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "var(--text-tertiary)",
          fontSize: "0.875rem",
        }}
      >
        {t("viewsFeatureNotEnabled")}
      </Box>
    );
  }

  if (viewLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          gap: "12px",
          color: "var(--text-secondary)",
        }}
      >
        <CircularProgress size={20} sx={{ color: "var(--accent)" }} />
        <span>{t("loadingView")}</span>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "var(--bg-deep)",
      }}
    >
      {/* View header */}
      <Box
        sx={{
          px: "16px",
          py: "12px",
          borderBottom: "1px solid var(--border-subtle)",
          backgroundColor: "var(--bg-surface)",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontSize: "1.125rem",
            fontWeight: 700,
            color: "var(--text-primary)",
            mb: "2px",
          }}
        >
          {view?.name ?? t("view")}
        </Box>
        {view?.description && (
          <Box
            sx={{
              fontSize: "0.8125rem",
              color: "var(--text-secondary)",
              lineHeight: 1.5,
            }}
          >
            {view.description}
          </Box>
        )}
        {view?.retentionDate && (
          <Box
            sx={{
              fontSize: "0.75rem",
              color: "var(--text-tertiary)",
              mt: "4px",
            }}
          >
            {t("expires")}: {formatExpiration(view.retentionDate)}
          </Box>
        )}
      </Box>

      {/* Filtered search */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <V2Search viewId={viewId} />
      </Box>
    </Box>
  );
}
